import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { CheckCircle, XCircle, Clock, AlertCircle, Save } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Attendance = () => {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => { fetchClasses(); }, [user?.school_id]);

  useEffect(() => {
    if (selectedClass && selectedDate) {
      fetchStudentsAndAttendance();
    }
  }, [selectedClass, selectedDate, user?.school_id]);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const handler = () => {
      fetchClasses();
      if (selectedClass && selectedDate) fetchStudentsAndAttendance();
    };
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [selectedClass, selectedDate, user?.school_id]);

  const fetchClasses = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const params = academicYearId ? { academic_year_id: academicYearId } : {};
      const response = await api.get('/classes', { params });
      setClasses(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch classes');
    }
  };

  const fetchStudentsAndAttendance = async () => {
    setLoading(true);
    try {
      const academicYearId = getSelectedAcademicYearId();
      const params = {
        class_id: selectedClass,
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      };
      const [studentsRes, attendanceRes] = await Promise.all([
        api.get('/students', { params }),
        api.get('/attendance', { params: { ...params, date: selectedDate } })
      ]);

      const studentsData = studentsRes.data.data || [];
      setStudents(studentsData);

      const attendanceMap = {};
      (attendanceRes.data.data || []).forEach(a => {
        attendanceMap[a.student_id] = a.status;
      });

      // Initialize attendance for all students: absent by default (no attendance marked yet)
      studentsData.forEach(s => {
        if (!attendanceMap[s.id]) {
          attendanceMap[s.id] = 'absent';
        }
      });

      setAttendance(attendanceMap);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (studentId, status) => {
    setAttendance(prev => ({ ...prev, [studentId]: status }));
  };

  const handleSave = async () => {
    try {
      const attendanceData = Object.entries(attendance).map(([student_id, status]) => ({
        student_id: parseInt(student_id),
        status,
      }));

      await api.post('/attendance/mark', {
        class_id: parseInt(selectedClass),
        date: selectedDate,
        attendance: attendanceData,
      });

      toast({ title: 'Success', description: 'Attendance saved successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save attendance', variant: 'destructive' });
    }
  };

  const statusOptions = [
    { value: 'present', label: 'Present', icon: CheckCircle, color: 'text-green-500' },
    { value: 'absent', label: 'Absent', icon: XCircle, color: 'text-red-500' },
    { value: 'late', label: 'Late', icon: Clock, color: 'text-amber-500' },
    { value: 'leave', label: 'Leave', icon: AlertCircle, color: 'text-blue-500' },
  ];

  const summary = {
    present: Object.values(attendance).filter(s => s === 'present').length,
    absent: Object.values(attendance).filter(s => s === 'absent').length,
    late: Object.values(attendance).filter(s => s === 'late').length,
    leave: Object.values(attendance).filter(s => s === 'leave').length,
  };

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Attendance</h1>
        <p className="text-slate-400">Mark daily attendance</p>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger className="w-full sm:w-48 bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="Select Class" />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                {classes.map(c => (
                  <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
            />
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" disabled={students.length === 0}>
              <Save className="w-4 h-4 mr-2" /> Save Attendance
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      {students.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {statusOptions.map(opt => (
            <Card key={opt.value} className="bg-slate-800 border-slate-700">
              <CardContent className="p-4 flex items-center gap-3">
                <opt.icon className={`w-8 h-8 ${opt.color}`} />
                <div>
                  <p className="text-2xl font-bold text-white">{summary[opt.value]}</p>
                  <p className="text-sm text-slate-400">{opt.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Attendance Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : !selectedClass ? (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="py-12 text-center">
            <p className="text-slate-400">Select a class to mark attendance</p>
          </CardContent>
        </Card>
      ) : students.length === 0 ? (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="py-12 text-center">
            <p className="text-slate-400">No students found in this class</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">Roll No</TableHead>
                  <TableHead className="text-slate-400">Student Name</TableHead>
                  <TableHead className="text-slate-400">Admission No</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {students.map((student, index) => (
                  <TableRow key={student.id} className="border-slate-700">
                    <TableCell className="text-white">{student.roll_no || index + 1}</TableCell>
                    <TableCell className="text-white">{student.first_name} {student.last_name}</TableCell>
                    <TableCell className="text-slate-400">{student.admission_no}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {statusOptions.map(opt => (
                          <Button
                            key={opt.value}
                            variant={attendance[student.id] === opt.value ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => handleStatusChange(student.id, opt.value)}
                            className={`${attendance[student.id] === opt.value 
                              ? opt.value === 'present' ? 'bg-green-600' 
                                : opt.value === 'absent' ? 'bg-red-600'
                                : opt.value === 'late' ? 'bg-amber-600'
                                : 'bg-blue-600'
                              : 'bg-slate-700 text-slate-400'}`}
                          >
                            <opt.icon className="w-4 h-4" />
                          </Button>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Attendance;
